import { Button } from 'primereact/button';
import { AddNewClassAttributesDialog } from './dialogs/add-new-class-attributes-dialog';
import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import globalSettingsBranches from '../../../../stores/global-settings-branches';
import {
  ClassAttributeList,
  ClassAttributeEntity,
} from '../../../../api/types/settings/settings-all';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Tag } from 'primereact/tag';
import { EditTextDialog } from '../../componenets/edit-entity-name-dialog/edit-text-dialog';
import { observer } from 'mobx-react-lite';
import './class-attributes.css';
import { editClassAttributeOperations } from './operations/edit-class-attribute-operations';
import { deleteAttributeOperation } from './operations/delete-attribute-operation';
import { editListNameOperation } from './operations/edit-list-name-operation';
import { ClassAttributeType } from '../../../../api/types/settings/class-attributes/class-attribute-types';
import { AddAttributeToExistingList } from './dialogs/add-attribute-to-existing-list';

export const ClassAttributes = observer(() => {
  const [addClassAttributes, setAddClassAttributes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [editAttribute, setEditAttribute] = useState<{
    listId: number;
    attribute: ClassAttributeEntity;
  } | null>(null);

  const [addAttributeToList, setAddAttributeToList] =
    useState<ClassAttributeList | null>(null);
  const [newAttributeName, setNewAttributeName] = useState('');
  const classAttributes: ClassAttributeList[] =
    globalSettingsBranches.classAttributes2;

  const [editList, setEditList] = useState<ClassAttributeList | undefined>();
  const handleListEdit = async (list: ClassAttributeList) => {
    setEditList(list);
  };
  const handleListEditClose = () => setEditList(undefined);

  const handleListEditSave = async (id: number, newValue: string) => {
    await editListNameOperation(id, newValue, setIsLoading);
  };

  const [selectedAttribute, setSelectedAttribute] =
    useState<ClassAttributeType | null>(null);

  const handleEditAttribute = async (id: number, newName: string) => {
    await editClassAttributeOperations(
      editAttribute?.listId,
      editAttribute?.attribute.id,
      newName,
      setIsLoading,
    );
  };

  const deleteAttributeById = async (
    attributeListId: number,
    attributeId: number,
  ) => await deleteAttributeOperation(attributeListId, attributeId);

  const renderAttributeTable = (
    listId: number,
    attributes: ClassAttributeEntity[],
  ) => {
    return (
      <div className="card p-0">
        <DataTable
          value={attributes}
          className="p-datatable-sm"
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו מאפיינים</div>
          }
          showGridlines
          stripedRows
        >
          <Column
            field="attributeName"
            header="שם המאפיין"
            style={{ textAlign: 'right', width: '40%' }}
          />
          <Column
            field="attributeType"
            header="סוג המאפיין"
            style={{ textAlign: 'right', width: '30%' }}
            body={(rowData) => (
              <Tag
                severity="info"
                value={rowData.attributeType}
                style={{ direction: 'rtl' }}
              />
            )}
          />
          <Column
            style={{ textAlign: 'right', width: '30%' }}
            body={(rowData: ClassAttributeEntity) => (
              <div className="flex justify-content-end gap-2">
                <Button
                  icon="pi pi-pencil"
                  text
                  severity="warning"
                  tooltip="ערוך"
                  onClick={() =>
                    setEditAttribute({ listId: listId, attribute: rowData })
                  }
                />
                <Button
                  icon="pi pi-trash"
                  text
                  severity="danger"
                  onClick={() => deleteAttributeById(listId, rowData.id)}
                  tooltip="מחק"
                />
              </div>
            )}
          />
        </DataTable>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="flex justify-content-between align-items-center mb-4">
        <h2 className="m-0" style={{ textAlign: 'right' }}>
          ניהול מאפייני כיתה
        </h2>
        <Button
          onClick={() => setAddClassAttributes(true)}
          severity="success"
          icon="pi pi-plus"
          tooltip="הוסף רשימת מאפיינים"
        />
      </div>

      <Accordion multiple className="mb-4">
        {classAttributes.map((list) => (
          <AccordionTab
            key={list.listId}
            className="class-attributes-accordion"
            header={
              <div
                className="flex align-items-center w-full px-2"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <span className="attribute-list-name">{list.listName}</span>
                  <Tag
                    value={`${list.attributes.length} מאפיינים`}
                    severity="info"
                    className="attribute-count-tag mx-2"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <Button
                    icon="pi pi-plus"
                    text
                    severity="success"
                    tooltip="הוסף מאפיין"
                    onClick={(e) => {
                      e.preventDefault();
                      setAddAttributeToList(list);
                    }}
                    className="attribute-add-button"
                  />

                  <Button
                    icon="pi pi-pencil"
                    text
                    severity="warning"
                    tooltip="ערוך"
                    onClick={() => handleListEdit(list)}
                  />
                </div>
              </div>
            }
          >
            <div className="pt-2">
              {renderAttributeTable(list.listId, list.attributes)}
            </div>
          </AccordionTab>
        ))}
      </Accordion>

      <style jsx global>{`
        .class-attributes-accordion .p-accordion-header-link {
          padding: 0.75rem 0.5rem;
          background-color: #f8fafc !important;
          border: none !important;
          border-radius: 8px !important;
        }

        .class-attributes-accordion .p-accordion-content {
          padding: 1rem;
          border: none !important;
          background-color: white;
        }

        .class-attributes-accordion .p-accordion-header-text {
          width: 100%;
        }
      `}</style>

      <Dialog
        header="הוספת רשימת מאפיינים"
        visible={addClassAttributes}
        onHide={() => setAddClassAttributes(false)}
        style={{
          width: '600px',
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
        modal
        dismissableMask
      >
        <AddNewClassAttributesDialog
          onClose={() => setAddClassAttributes(false)}
        />
      </Dialog>

      <EditTextDialog
        id={editAttribute?.attribute.id}
        value={editAttribute?.attribute.attributeName}
        title="עריכת מאפיין"
        label="שם המאפיין"
        loading={isLoading}
        onSave={handleEditAttribute}
        onHide={() => setEditAttribute(null)}
      />

      <EditTextDialog
        id={editList?.listId}
        value={editList?.listName}
        title="עריכת מאפיין"
        label="שם המאפיין"
        loading={isLoading}
        onSave={handleListEditSave}
        onHide={() => handleListEditClose()}
      />

      <Dialog
        visible={!!addAttributeToList}
        onHide={() => {
          setAddAttributeToList(null);
          setNewAttributeName('');
        }}
        header={`הוספת מאפיין לרשימה: ${addAttributeToList?.listName}`}
        modal
        style={{ width: '400px' }}
      >
        <AddAttributeToExistingList
          listId={addAttributeToList?.listId}
          onHilde={() => {
            setAddAttributeToList(null);
            setNewAttributeName('');
          }}
        />
      </Dialog>
    </div>
  );
});
